import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useReactTableNoMemo } from "#shared/helpers/useReactTableNoMemo";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { Group } from "#shared/view/components/Group";
import { Pagination } from "#shared/view/components/Pagination";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { Table } from "#shared/view/designSystem/Table";
import { formatDate, formatPrice } from "shared";

import { Material } from "../../materials/infra/fetchCustomMaterials.api";

const PAGE_SIZE = 15;

type MaterialsSectionTableProps = {
  data: Material[];
  onAddRow: () => void;
  onEditRow: (rowId: string) => void;
  onRemoveRow: (rowId: string) => void;
};

type MaterialWithIndex = Material & {
  originalIndex: number;
};

export const MaterialsSectionTable = ({
  data,
  onAddRow,
  onEditRow,
  onRemoveRow,
}: MaterialsSectionTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<MaterialWithIndex>[]>(
    () => [
      {
        accessorKey: "type",
        header: t("settings.materials.type"),
        meta: { width: "10%" },
        cell: ({ row }) => t(`qrl.materialTypes.${row.original.type}`),
      },
      {
        accessorKey: "name",
        header: t("settings.materials.name"),
        meta: { width: "15%" },
      },
      {
        accessorKey: "materialGrade",
        header: t("qrl.materialGrade"),
        meta: { width: "10%" },
        cell: ({ row }) => t(`qrl.materialGrades.${row.original.materialGrade}`),
      },
      {
        accessorKey: "length",
        header: t("settings.materials.length"),
        meta: { width: "8%" },
      },
      {
        accessorKey: "width",
        header: t("settings.materials.width"),
        meta: { width: "8%" },
      },
      {
        accessorKey: "height",
        header: t("settings.materials.height"),
        meta: { width: "8%" },
      },
      {
        accessorKey: "tubeThickness",
        header: t("settings.materials.tubeThickness"),
        meta: { width: "8%" },
        cell: ({ row }) => row.original.tubeThickness ?? "-",
      },
      {
        accessorKey: "price",
        header: t("settings.materials.price"),
        meta: { width: "8%" },
        cell: ({ row }) => formatPrice(row.original.price),
      },
      {
        accessorKey: "updatedAt",
        header: t("settings.materials.updatedAt"),
        meta: { width: "8%" },
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
      {
        id: "actions",
        header: "",
        meta: { width: "5%" },
        cell: ({ row }) => (
          <Group gap="xs" justify="end" wrap="nowrap">
            <ActionIcon
              onClick={() => onEditRow(row.id)}
              variant="subtle"
              size="sm"
              aria-label={`Edit row ${row.index}`}
            >
              <Icon name="edit" size={14} />
            </ActionIcon>
            <ActionIcon
              onClick={() => onRemoveRow(row.id)}
              variant="subtle"
              size="sm"
              aria-label={`Remove row ${row.index}`}
            >
              <Icon name="trash" size={14} />
            </ActionIcon>
          </Group>
        ),
      },
    ],
    [onEditRow, onRemoveRow, t],
  );

  const sortedData = useMemo<MaterialWithIndex[]>(() => {
    return data
      .map((item, originalIndex) => ({ ...item, originalIndex }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [data]);

  const { table, rows, headerGroups, leafColumns, pageCount, state } = useReactTableNoMemo({
    data: sortedData,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE, pageIndex: 0 } },
    autoResetPageIndex: false,
  });

  return (
    <Stack gap="sm">
      <Table highlightOnHover stickyHeader style={{ tableLayout: "fixed" }}>
        <colgroup>
          {leafColumns.map((col) => {
            const columnsCount = leafColumns.length;
            const defaultWidth = 100 / columnsCount;
            const w =
              (col.columnDef.meta as { width?: string | number } | undefined)?.width ??
              `${defaultWidth}%`;
            return (
              <col key={col.id} style={{ width: typeof w === "number" ? `${w}px` : String(w) }} />
            );
          })}
        </colgroup>
        <Table.Thead>
          {headerGroups.map((headerGroup) => (
            <Table.Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.Th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </Table.Th>
              ))}
            </Table.Tr>
          ))}
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row) => (
            <Table.Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Group justify="space-between">
        <Button onClick={onAddRow} variant="outline" leftSection={<Icon name="plus" />}>
          {t("common.add")}
        </Button>
        {pageCount > 1 && (
          <Pagination
            total={pageCount}
            value={state.pagination.pageIndex + 1}
            onChange={(page) => table.setPageIndex(page - 1)}
          />
        )}
      </Group>
    </Stack>
  );
};
